import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  listStaffSubmissionVersions,
  getStaffSubmissionVersionDetail,
  downloadStaffSubmissionVersionPdf,
  listCoordinatorSubmissionVersions,
  getCoordinatorSubmissionVersionDetail,
  downloadCoordinatorSubmissionVersionPdf,
  listSubmissionPdfPacks,
  listCoordinatorSubmissionPdfPacks,
} from '../../../services/eventApi';
import {
  shouldHideQuestionInAnswerList,
} from './applicationQuestionRenderers';
import SubmissionAnswerValue from './SubmissionAnswerValue';
import { deliverAttachmentBlob } from '../../../utils/eventAttachmentDelivery';
import { resolvePeopleLabels } from '../../../utils/applicationPeopleLabels';
import { downloadSubmissionPdfPacks } from '../../../utils/downloadSubmissionPdfPacks';
import { DEFAULT_PDF_PACK_KEY } from '../../../utils/applicationFormPdfPacks';
import EventLoadingState from '../Common/EventLoadingState';

function formatDateTime(value) {
  if (!value) return '—';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function sourceLabel(source) {
  if (source === 'PORTAL_UPDATE') return 'Portal update (e.g. screening hold)';
  if (source === 'PORTAL_RESUBMIT') return 'Portal resubmit';
  return source || '—';
}

/** Stable id for list/detail/pdf calls (handles plain API ids and BSON-style objects). */
function versionRowId(v) {
  const raw = v?._id ?? v?.id;
  if (raw != null && raw !== '') return String(raw);
  return `vn-${v?.versionNumber}-${v?.createdAt || ''}`;
}

const AnswerBlock = ({ title, answers, questionById, attachmentDeliveryMode = 'open' }) => {
  const visible = (answers || []).filter((a) => !shouldHideQuestionInAnswerList(a?.questionType));
  return (
    <div className="border border-gray-100 rounded-lg overflow-hidden">
      <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
        <h4 className="text-xs font-semibold text-gray-700">{title}</h4>
      </div>
      {visible.length ? (
        <div className="divide-y divide-gray-100">
          {visible.map((answer, idx) => (
            <div key={`${answer.questionId || idx}`} className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-2 px-3 py-2">
              <div className="text-sm font-medium text-gray-700">{answer.questionLabel || answer.questionId || `Question ${idx + 1}`}</div>
              <div className="text-sm text-gray-900 break-words">
                <SubmissionAnswerValue answer={answer} questionById={questionById} attachmentDeliveryMode={attachmentDeliveryMode} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="px-3 py-4 text-sm text-gray-500 text-center">No answers.</div>
      )}
    </div>
  );
};

const DetailGrid = ({ items }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
    {items.map((item) => (
      <div key={item.label}>
        <p className="text-xs uppercase tracking-wide text-gray-400 mb-0.5">{item.label}</p>
        <div className="text-sm font-medium text-gray-900 break-words">{item.value || '—'}</div>
      </div>
    ))}
  </div>
);

/**
 * Prior portal-save snapshots + PDF download (staff event submission detail + coordinator portal form).
 *
 * @param {{ variant: 'staff' | 'portal', eventId?: string, submissionId: string, token?: string, refreshKey?: string|number, questionById?: Map }} props
 */
export default function SubmissionVersionHistory({ variant, eventId, submissionId, token, refreshKey, peopleLabels: peopleLabelsProp, questionById }) {
  const peopleLabels = peopleLabelsProp || resolvePeopleLabels();
  const attachmentDeliveryMode = variant === 'staff' ? 'open' : 'download';
  const [loading, setLoading] = useState(true);
  const [versions, setVersions] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [detailLoadingId, setDetailLoadingId] = useState(null);
  const [detailMap, setDetailMap] = useState({});
  const [pdfBusyId, setPdfBusyId] = useState('');

  const loadList = useCallback(async () => {
    if (!submissionId || (variant === 'staff' && !eventId) || (variant === 'portal' && !token)) {
      setVersions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res =
        variant === 'staff'
          ? await listStaffSubmissionVersions(eventId, submissionId)
          : await listCoordinatorSubmissionVersions(token, submissionId);
      setVersions(res?.data?.data?.versions || []);
    } catch (err) {
      if (err?.response?.status !== 404) {
        toast.error(err?.response?.data?.message || 'Could not load revision history');
      }
      setVersions([]);
    } finally {
      setLoading(false);
    }
  }, [variant, eventId, submissionId, token]);

  useEffect(() => {
    loadList();
  }, [loadList, refreshKey]);

  const toggleExpand = async (versionId) => {
    if (expandedId === versionId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(versionId);
    if (detailMap[versionId]) return;
    setDetailLoadingId(versionId);
    try {
      const res =
        variant === 'staff'
          ? await getStaffSubmissionVersionDetail(eventId, submissionId, versionId)
          : await getCoordinatorSubmissionVersionDetail(token, submissionId, versionId);
      const payload = res?.data?.data;
      setDetailMap((prev) => ({ ...prev, [versionId]: payload }));
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not load revision');
    } finally {
      setDetailLoadingId(null);
    }
  };

  const handlePdf = async (versionId, versionNumber, applicationNumber) => {
    setPdfBusyId(versionId);
    try {
      const result = await downloadSubmissionPdfPacks({
        listPacks: () => (
          variant === 'staff'
            ? listSubmissionPdfPacks(submissionId)
            : listCoordinatorSubmissionPdfPacks(submissionId)
        ),
        downloadPack: (packKey) => (
          variant === 'staff'
            ? downloadStaffSubmissionVersionPdf(eventId, submissionId, versionId, packKey)
            : downloadCoordinatorSubmissionVersionPdf(token, submissionId, versionId, packKey)
        ),
        applicationNumber: `${applicationNumber || 'application'}-v${versionNumber}`,
        mode: variant === 'staff' ? 'open' : 'download',
      });
      if (result.zipped) {
        toast.success(`Downloaded ${result.packCount} revision PDFs as a ZIP (${result.titles.join(', ')})`);
      } else if (variant === 'portal') {
        toast.success('Revision PDF downloaded');
      }
    } catch (err) {
      try {
        const res =
          variant === 'staff'
            ? await downloadStaffSubmissionVersionPdf(eventId, submissionId, versionId, DEFAULT_PDF_PACK_KEY)
            : await downloadCoordinatorSubmissionVersionPdf(token, submissionId, versionId, DEFAULT_PDF_PACK_KEY);
        deliverAttachmentBlob(res.data, {
          fileName: `application-v${versionNumber}.pdf`,
          mimeType: 'application/pdf',
          deliveryMode: variant === 'staff' ? 'open' : 'download',
        });
      } catch (fallbackErr) {
        toast.error(fallbackErr?.response?.data?.message || err?.message || 'PDF could not be opened');
      }
    } finally {
      setPdfBusyId('');
    }
  };

  if (!submissionId || (variant === 'staff' && !eventId) || (variant === 'portal' && !token)) {
    return null;
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 mb-5">
      <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-1">Previous revisions</h2>
      <p className="text-xs text-gray-500 mb-4">
        Each row is a snapshot of your application <strong>before</strong> a portal save. Download the PDF pack(s) as they would have been generated for that snapshot (ZIP when the form has multiple PDF outputs).
      </p>

      {loading ? (
        <EventLoadingState message="Loading history..." />
      ) : !versions.length ? (
        <p className="text-sm text-gray-600 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
          No earlier snapshots yet. After you save changes from the portal, the previous answers appear here with a matching PDF.
        </p>
      ) : (
        <div className="space-y-2">
          {versions.map((v) => {
            const vid = versionRowId(v);
            const expanded = expandedId === vid;
            const detail = detailMap[vid];
            const snap = detail?.snapshot;

            return (
              <div key={vid} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 bg-indigo-50/60">
                  <div className="text-sm">
                    <span className="font-semibold text-gray-900">Revision #{v.versionNumber}</span>
                    <span className="text-gray-500 mx-2">·</span>
                    <span className="text-gray-600">{formatDateTime(v.createdAt)}</span>
                    <span className="text-gray-500 mx-2">·</span>
                    <span className="text-gray-700">{sourceLabel(v.source)}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => toggleExpand(vid)}
                      className="text-xs font-medium px-2 py-1 rounded border border-indigo-300 text-indigo-800 bg-white hover:bg-indigo-50"
                    >
                      {expanded ? 'Hide answers' : 'View answers'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePdf(vid, v.versionNumber, snap?.applicationNumber || v.applicationNumber)}
                      disabled={pdfBusyId === vid}
                      className="text-xs font-medium px-2 py-1 rounded border border-gray-300 text-gray-800 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      {pdfBusyId === vid
                        ? 'Preparing…'
                        : (variant === 'staff' ? 'View PDFs' : 'Download PDFs')}
                    </button>
                  </div>
                </div>
                {expanded && (
                  <div className="px-3 py-3 bg-white border-t border-gray-100 space-y-4">
                    {detailLoadingId === vid && !snap ? (
                      <EventLoadingState variant="inline" message="Loading..." />
                    ) : snap ? (
                      <>
                        <DetailGrid
                          items={[
                            { label: 'Application code', value: snap.applicationNumber },
                            { label: 'Status', value: snap.status },
                            { label: 'Industry', value: snap.industryName },
                            { label: 'Category', value: snap.categoryName },
                            { label: 'Coordinator', value: snap.submitterName },
                            { label: 'Email', value: snap.submitterEmail },
                          ]}
                        />
                        <AnswerBlock title="Common answers" answers={snap.answers} questionById={questionById} attachmentDeliveryMode={attachmentDeliveryMode} />
                        {(snap.people || []).map((person, idx) => (
                          <div key={idx} className="space-y-2">
                            <p className="text-xs font-semibold text-gray-600">
                              {peopleLabels.singular} {Array.isArray(snap.people) && snap.people.length > 1 ? idx + 1 : ''}{' '}
                              {person.categoryName ? (
                                <span className="font-normal text-gray-500">({person.categoryName})</span>
                              ) : null}
                            </p>
                            <DetailGrid
                              items={[
                                { label: 'Name', value: person.fullName },
                                { label: 'Email', value: person.email },
                                { label: 'Mobile', value: person.phone },
                              ].filter((x) => x.value)}
                            />
                            <AnswerBlock title={`${peopleLabels.singular} answers`} answers={person.answers} questionById={questionById} attachmentDeliveryMode={attachmentDeliveryMode} />
                          </div>
                        ))}
                      </>
                    ) : (
                      <p className="text-sm text-red-600">Could not load this revision.</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
